import { useRef, useState, type ChangeEvent, type FormEvent } from 'react'
import { Box, Button, Input, Stack } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { StepHeader } from '@/features/transfer/components/StepHeader'
import { IBAN_MAX_LENGTH, NAME_MAX_LENGTH } from '@/features/transfer/model/constants'
import {
  normalizeIban,
  validateIban,
  validateName,
} from '@/features/transfer/model/validation'
import { checkIbanReachability } from '@/features/transfer/api/checkIbanReachability'

type ContactValues = { name: string; iban: string }

type ContactStepProps = {
  title: string
  name: string
  iban: string
  onBack: () => void
  onContinue: (values: ContactValues) => void
}

type Errors = { name: string | null; iban: string | null }

export const ContactStep = ({
  title,
  name: initialName,
  iban: initialIban,
  onBack,
  onContinue,
}: ContactStepProps) => {
  const { t } = useTranslation()
  const [name, setName] = useState(initialName)
  const [iban, setIban] = useState(initialIban)
  const [errors, setErrors] = useState<Errors>({ name: null, iban: null })
  const [isChecking, setIsChecking] = useState(false)
  const nameRef = useRef<HTMLInputElement>(null)
  const ibanRef = useRef<HTMLInputElement>(null)

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
    if (errors.name) setErrors(prev => ({ ...prev, name: null }))
  }

  const handleIbanChange = (event: ChangeEvent<HTMLInputElement>) => {
    setIban(event.target.value.replace(/\s/g, '').toUpperCase())
    if (errors.iban) setErrors(prev => ({ ...prev, iban: null }))
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isChecking) return

    const next: Errors = {
      name: validateName(name),
      iban: validateIban(iban),
    }
    setErrors(next)
    const firstInvalid = [
      { error: next.name, ref: nameRef },
      { error: next.iban, ref: ibanRef },
    ].find(field => field.error)
    if (firstInvalid) {
      firstInvalid.ref.current?.focus()
      return
    }

    const normalized = normalizeIban(iban)
    setIsChecking(true)
    try {
      const reachable = await checkIbanReachability(normalized)
      if (!reachable) {
        setErrors(prev => ({ ...prev, iban: t('validation.ibanNotReachable') }))
        ibanRef.current?.focus()
        return
      }
    } catch {
      // Fail open: an unconfirmed check (network error, 5xx, timeout)
      // shouldn't block a structurally valid IBAN from proceeding.
    } finally {
      setIsChecking(false)
    }

    onContinue({ name: name.trim(), iban: normalized })
  }

  return (
    <Stack direction='vertical' space='48'>
      <StepHeader title={title} onBack={onBack} />
      <form onSubmit={handleSubmit} noValidate>
        <Stack direction='vertical' space='12'>
          <Stack direction='vertical' space='8'>
            <Box backgroundColor='neutral' borderRadius='8'>
              <Input
                ref={nameRef}
                name='name'
                label={t('transfer.contact.fullNameLabel')}
                value={name}
                onChange={handleNameChange}
                autoComplete='off'
                maxLength={NAME_MAX_LENGTH}
                required
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? 'name-error' : undefined}
              />
            </Box>
            {errors.name ? (
              <div id='name-error'>
                <Input.Hint variant='error'>{errors.name}</Input.Hint>
              </div>
            ) : null}
          </Stack>

          <Stack direction='vertical' space='8'>
            <Box backgroundColor='neutral' borderRadius='8'>
              <Input
                ref={ibanRef}
                name='iban'
                label={t('transfer.contact.ibanLabel')}
                value={iban}
                onChange={handleIbanChange}
                autoComplete='off'
                maxLength={IBAN_MAX_LENGTH}
                required
                aria-invalid={Boolean(errors.iban)}
                aria-describedby={errors.iban ? 'iban-error' : undefined}
              />
            </Box>
            {errors.iban ? (
              <div id='iban-error'>
                <Input.Hint variant='error'>{errors.iban}</Input.Hint>
              </div>
            ) : null}
          </Stack>

          <Stack direction='horizontal'>
            <Button type='submit' variant='primary' color='teal' loading={isChecking}>
              {t('transfer.contact.continueButton')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Stack>
  )
}
